/**
 * Транспорт до сервиса.
 *
 * Формат ответа один на весь API: `{ error: [], results }`. Разбирается он
 * здесь, в одном месте, — иначе каждый вызов начинал бы с проверки `error`
 * и рано или поздно кто-то её забудет.
 */

export type ApiErrorItem = { code: string; message: string; field?: string };

export class ApiFailure extends Error {
  readonly status: number;
  readonly items: ApiErrorItem[];

  constructor(status: number, items: ApiErrorItem[]) {
    super(items[0]?.message ?? "Ошибка сервера");
    this.status = status;
    this.items = items;
  }

  get code(): string {
    return this.items[0]?.code ?? "UNKNOWN";
  }
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  // FormData: границу multipart браузер пишет в Content-Type сам.
  const json = !(init?.body instanceof FormData);
  const response = await fetch(path, {
    ...init,
    headers: { ...(json ? { "Content-Type": "application/json" } : {}), ...(init?.headers ?? {}) },
    // Сессия живёт в httpOnly-cookie, поэтому запрос обязан её нести.
    credentials: "same-origin",
  });

  let payload: { error?: ApiErrorItem[]; results?: T } | null = null;
  try {
    payload = (await response.json()) as {
      error?: ApiErrorItem[];
      results?: T;
    };
  } catch {
    // 413 без JSON — отказ шлюза nginx (`client_max_body_size`), до сервиса
    // запрос не дошёл; свой 413 сервис отдаёт JSON-ом.
    if (response.status === 413) {
      throw new ApiFailure(413, [
        { code: "GATEWAY_TOO_LARGE", message: "Файл больше, чем пропускает шлюз сервиса" },
      ]);
    }
    throw new ApiFailure(response.status, [
      { code: "BAD_RESPONSE", message: "Сервис вернул не JSON" },
    ]);
  }

  const errors = payload?.error ?? [];
  if (errors.length > 0) throw new ApiFailure(response.status, errors);
  if (!response.ok) {
    throw new ApiFailure(response.status, [
      { code: "HTTP_ERROR", message: `Сервис ответил ${response.status}` },
    ]);
  }
  return payload?.results as T;
}

/**
 * Собрать query-строку, выбросив пустые значения: они превращают ссылку в мусор.
 * Массив уходит ПОВТОРАМИ ключа (`supplier_id=1&supplier_id=2`) — так ручки
 * принимают список.
 */
export function query(
  params: Record<string, string | number | boolean | null | undefined | readonly string[]>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const one of value) if (one) search.append(key, one);
      continue;
    }
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === false
    )
      continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}
